import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Group, Modal, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconBellRinging, IconClock } from '@tabler/icons-react';
import { useAuth } from '../../../../auth/AuthContext';
import { useApp } from '../../../../context/AppContext';
import { primaryColor } from '../../../../constants/theme';
import { notifyBookingRequest } from '../../../../services/BookingService';
import { ErrorModal } from '../error/ErrorModal';

interface NoAvailabilityNotifyButtonProps {
  professionalId: string;
}

export const NoAvailabilityNotifyButton = ({
  professionalId,
}: NoAvailabilityNotifyButtonProps) => {
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const { token } = useApp();
  const { userId } = useAuth();
  const storageKey = `notify_me_${userId ?? 'anonymous'}_${professionalId}`;

  useEffect(() => {
    const checkDisabledState = () => {
      try {
        const stored = localStorage.getItem(storageKey);

        if (!stored) {
          setIsDisabled(false);
          return;
        }

        const timestamp = parseInt(stored, 10);
        const now = Date.now();
        const hours24 = 24 * 60 * 60 * 1000;

        if (now - timestamp < hours24) {
          setIsDisabled(true);
        } else {
          localStorage.removeItem(storageKey);
          setIsDisabled(false);
        }
      } catch (error) {
        console.error('Error reading from localStorage', error);
      }
    };

    checkDisabledState();
  }, [storageKey]);

  const {
    mutate: sendNotifyRequest,
    isPending,
    isError,
    reset,
  } = useMutation({
    mutationFn: async () => {
      await notifyBookingRequest(professionalId, token || '');
    },
    onSuccess: () => {
      setShowModal(false);
      setShowSuccessModal(true);
      setIsDisabled(true);

      try {
        localStorage.setItem(storageKey, Date.now().toString());
      } catch (error) {
        console.error('Error writing to localStorage', error);
      }
    },
  });

  return (
    <>
      <ErrorModal
        isInError={isError}
        errorMessage="There was an error sending your notify request. Please try again."
        dismissAction={() => reset()}
        retryAction={() => sendNotifyRequest()}
        isLoading={isPending}
        testId="no-availability-notify-error-modal"
      />
      <Modal
        opened={showModal && !isError}
        onClose={() => setShowModal(false)}
        withCloseButton={false}
        centered
        data-testid="no-availability-notify-modal"
      >
        <Stack gap={20}>
          <Stack justify="center" align="center" gap={15}>
            <ThemeIcon
              size={60}
              radius="100%"
              variant="light"
              color={primaryColor}
              data-testid="no-availability-notify-modal-icon"
            >
              <IconBellRinging size={40} />
            </ThemeIcon>
            <Title
              order={3}
              fw="bold"
              ta="center"
              data-testid="no-availability-notify-modal-title"
            >
              Notify Me
            </Title>
            <Text
              c="dimmed"
              size="md"
              ta="center"
              lh={1.3}
              data-testid="no-availability-notify-modal-description"
            >
              Let the creator know you are interested in a conversation.
            </Text>

            <Paper
              bg="gray.0"
              p="sm"
              radius="md"
              w="100%"
              data-testid="no-availability-notify-modal-limit"
            >
              <Group gap={10} wrap="nowrap" align="center" justify="center">
                <IconClock size={20} color="gray" style={{ minWidth: 20 }} />
                <Text size="sm" c="dimmed" ta="center">
                  You can only send a request once per day
                </Text>
              </Group>
            </Paper>

            <Text
              c="dimmed"
              size="md"
              ta="center"
              fs="italic"
              data-testid="no-availability-notify-modal-footer"
            >
              You will receive an email when new time slots are available.
            </Text>
          </Stack>
          <Group gap={10} justify="center" w="100%" mt={5}>
            <Button
              variant="outline"
              w="48%"
              color={primaryColor}
              onClick={() => setShowModal(false)}
              data-testid="no-availability-notify-cancel"
            >
              Cancel
            </Button>
            <Button
              w="48%"
              color={primaryColor}
              onClick={() => sendNotifyRequest()}
              loading={isPending}
              data-testid="no-availability-notify-confirm"
            >
              Notify
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Modal
        opened={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        withCloseButton={false}
        centered
        data-testid="no-availability-notify-success-modal"
      >
        <Stack gap={20}>
          <Stack justify="center" align="center" gap={15}>
            <ThemeIcon
              size={60}
              radius="100%"
              variant="light"
              color={primaryColor}
              data-testid="no-availability-notify-success-icon"
            >
              <IconBellRinging size={40} />
            </ThemeIcon>
            <Title
              order={3}
              fw="bold"
              ta="center"
              data-testid="no-availability-notify-success-title"
            >
              You&apos;re on the list
            </Title>
            <Text
              c="dimmed"
              size="md"
              ta="center"
              lh={1.3}
              data-testid="no-availability-notify-success-description"
            >
              We sent your request. You&apos;ll get an email when new time slots are available.
            </Text>
          </Stack>
          <Button
            color={primaryColor}
            onClick={() => setShowSuccessModal(false)}
            data-testid="no-availability-notify-success-close"
            fullWidth
          >
            OK
          </Button>
        </Stack>
      </Modal>
      <Button
        color={primaryColor}
        maw={400}
        fullWidth
        onClick={() => setShowModal(true)}
        disabled={isDisabled}
        data-testid="no-availability-notify-button"
      >
        {isDisabled ? 'Notified Recently' : 'Notify Me'}
      </Button>
    </>
  );
};